import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workedWith, setWorkedWith] = useState(new Set());

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/users/history");
      setWorkedWith(new Set(data?.pro_user_ids || []));
    } catch { setWorkedWith(new Set()); }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("lima_token");
    if (!token) { setUser(false); setWorkedWith(new Set()); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.role === "client") loadHistory();
    } catch { setUser(false); localStorage.removeItem("lima_token"); }
    setLoading(false);
  }, [loadHistory]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfaRequired: true, mfaToken: data.mfa_token };
    localStorage.setItem("lima_token", data.token);
    setUser(data.user);
    if (data.user.role === "client") loadHistory();
    return { mfaRequired: false, user: data.user };
  };

  const loginMfaVerify = async (mfaToken, code) => {
    const { data } = await api.post("/auth/mfa/login-verify", { mfa_token: mfaToken, code });
    localStorage.setItem("lima_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("lima_token", data.token);
    setUser(data.user);
    if (data.user.role === "client") loadHistory();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("lima_token");
    setUser(false);
    setWorkedWith(new Set());
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginMfaVerify, register, logout, refresh, workedWith, refreshHistory: loadHistory }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
